import {Injectable} from '@angular/core';
import {Storage} from '@ionic/storage';
import {Regions} from '../../enums/regions.enum';
import * as moment from 'moment';
import { AiGeneral2WeekImprovementSummary } from '../../components/ai-trends/interfaces/ai-general-2-week-improvement-summary.type';
import { SessionBreakdownType } from '../../components/ai-trends/enums/session-breakdown-type.enum';
import { PlayerAiTrendsEnum } from '../../components/ai-trends/enums/player-ai-trends.enum';
import { AiGeneralSquadImprovements } from '../../interfaces/ai/ai-general-2-week-improvement-summary';

@Injectable({
    providedIn: 'root'
})
export class LocalStorageService {
    favouritePitches: string[];
    private readonly TRENDS_DATA_PREFIX = 'playerTrends_';
    private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes (was 24 hours)

    constructor(private storage: Storage) {
        storage.create().then();
        this.getFavouritePitches();
    }

    // Logic to save favourite pitches to local storage

    getFavouritePitches() {
        this.storage.get('favPitches').then((pitches) => {
            if (Array.isArray(pitches) && pitches.length > 0) {
                this.favouritePitches = pitches;
            }
        });
    }

    saveFavouritePitches() {
        this.storage.set('favPitches', this.favouritePitches).then();
    }

    setFavouritePitch(pitchId: string) {
        if (!this.favouritePitches) {
            this.favouritePitches = [];
        }

        this.favouritePitches.push(pitchId);
        this.saveFavouritePitches();
    }

    removeFavouritePitch(pitchId: string) {
        if (Array.isArray(this.favouritePitches) && this.favouritePitches.length > 0) {
            if (this.favouritePitches.findIndex(pId => pId === pitchId) !== -1) {
                this.favouritePitches = this.favouritePitches.filter(pId => pId !== pitchId);
                this.saveFavouritePitches();
            }
        }
    }

    getFirstTimeFlag(usedId: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.storage.get(usedId).then((val) => {
                if (!val) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

    getUserHasNotBeenShownInitialUpgradePrompt(): Promise<boolean> {
        return new Promise((resolve) => {
            this.storage.get('shownUpgradeModal1').then((val) => {
                if (!val) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }


    setFirstTimeFlag(usedId: string) {
        this.storage.set(usedId, true).then();
    }

    getUsersRegion(): Promise<any> {
        return new Promise((resolve) => {
            this.storage.get('region').then((val) => {
                resolve(val);
            });
        });
    }

    setUsersRegion(region: Regions) {
        this.storage.set('region', region).then();
    }

    setUserHasBeenShownInitialUpgradePrompt() {
        this.storage.set('shownUpgradeModal1', true).then();
    }

    async getTimestampToShowBlackFridayPromoModal(): Promise<number> {
        try {
            const val = await this.storage.get('timestampToShowBlackFridayModal');
            
            // Return current time if value is invalid
            if (!val || val <= 0) {
                return moment().valueOf();
            }
            return val;
        } catch (error) {
            console.error('Error getting timestamp:', error);
            return moment().valueOf(); // Fallback to current time
        }
    }

    async setTimestampToShowBlackFridayPromoModal(timestamp: number): Promise<void> {
        try {
            await this.storage.set('timestampToShowBlackFridayModal', timestamp);
            
            // Verify the value was set
            const verifyValue = await this.storage.get('timestampToShowBlackFridayModal');
        } catch (error) {
            console.error('Error setting timestamp:', error);
        }
    }

    async getTimestampToShowEarlyPromoModal(): Promise<number> {
        try {
            const val = await this.storage.get('timestampToShowEarlyPromoModal');
            
            // Return current time if value is invalid
            if (!val || val <= 0) {
                return moment().valueOf();
            }
            return val;
        } catch (error) {
            console.error('Error getting early promo timestamp:', error);
            return moment().valueOf(); // Fallback to current time
        }
    }

    async setTimestampToShowEarlyPromoModal(timestamp: number): Promise<void> {
        try {
            await this.storage.set('timestampToShowEarlyPromoModal', timestamp);
            
            // Verify the value was set
            const verifyValue = await this.storage.get('timestampToShowEarlyPromoModal');
        } catch (error) {
            console.error('Error setting early promo timestamp:', error);
        }
    }

    // Player Trends Local Storage Methods
    private generatePlayerTrendsKey(squadId: string, sessionBreakdownType: SessionBreakdownType, playerAiTrendsEnum: PlayerAiTrendsEnum): string {
        return `playerTrends_${squadId}_${sessionBreakdownType}_${playerAiTrendsEnum}`;
    }

    async getPlayerTrendsData(
        squadId: string,
        sessionBreakdownType: number,
        metricGroup: string
    ): Promise<AiGeneralSquadImprovements | null> {
        const key = this.getTrendsKey(squadId, sessionBreakdownType, metricGroup);
        const data = localStorage.getItem(key);
        
        if (!data) {
            return null;
        }

        try {
            const parsed = JSON.parse(data);
            const timestamp = parsed.timestamp;
            
            // Check if cache is expired
            if (Date.now() - timestamp > this.CACHE_DURATION) {
                localStorage.removeItem(key);
                return null;
            }
            
            return parsed.data;
        } catch (e) {
            console.error('[LocalStorage] Error parsing trends data:', e);
            return null;
        }
    }

    setPlayerTrendsData(
        squadId: string,
        sessionBreakdownType: number,
        metricGroup: string,
        data: AiGeneralSquadImprovements
    ): void {
        const key = this.getTrendsKey(squadId, sessionBreakdownType, metricGroup);
        const toStore = {
            timestamp: Date.now(),
            data
        };
        
        try {
            localStorage.setItem(key, JSON.stringify(toStore));
        } catch (e) {
            console.error('[LocalStorage] Error storing trends data:', e);
        }
    }

    private getTrendsKey(squadId: string, sessionBreakdownType: number, metricGroup: string): string {
        return `${this.TRENDS_DATA_PREFIX}${squadId}_${sessionBreakdownType}_${metricGroup}`;
    }

    async removePlayerTrendsData(squadId: string, sessionBreakdownType: SessionBreakdownType, playerAiTrendsEnum: PlayerAiTrendsEnum): Promise<void> {
        try {
            const key = this.generatePlayerTrendsKey(squadId, sessionBreakdownType, playerAiTrendsEnum);
            await this.storage.remove(key);
        } catch (error) {
            console.error('[LocalStorage] Error removing player trends data:', error);
        }
    }

    

}
